import 'mutationobserver-shim'
import Vue from 'vue'
import VueMeta from 'vue-meta'
import App from './App.vue'
// import './registerServiceWorker' // disabled service worker
import router from './router'
import store from './store'
import i18n from './i18n'
import { Datetime } from 'vue-datetime'
import 'vue-datetime/dist/vue-datetime.css'
import { setAuthToken } from './utils/api'
import VModal from 'vue-js-modal'
import * as VueGoogleMaps from 'vue2-google-maps'
import * as Sentry from '@sentry/browser'
import { Integrations } from '@sentry/tracing'
import config from './utils/config'
import moment from 'moment'
import { Settings } from 'luxon'
import VueGtag from 'vue-gtag'
import posthog from 'posthog-js'
import analytics from './utils/analytics'
import {
  getConsent,
  applyConsent,
  hasConsent,
  CONSENT_ACCEPTED
} from './utils/consent'
import './assets/tailwind.css'

const token = localStorage.getItem('auth-token')
if (token) {
  setAuthToken(token)
}

window.commitRef = config.commitRef
window.cachedCommitRef = config.cachedCommitRef
window.buildId = config.buildId

moment.locale('nl')
Settings.defaultLocale = 'nl'
Settings.defaultZoneName = 'Europe/Amsterdam'

if (process.env.NODE_ENV !== 'development') {
  // setup sentry tracking
  Sentry.init({
    Vue,
    dsn:
      'https://46e1f17a48244f6a903fff4cffe2a745@o508340.ingest.sentry.io/5600705',
    autoSessionTracking: true,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.1,
    environment: process.env.NODE_ENV,
    release: `invi-frontend@${process.env.VUE_APP_COMMIT_REF}`
  })

  // setup posthog tracking — cookieless_mode 'on_reject': with consent PostHog
  // tracks normally, without consent it only counts anonymously via a
  // server-side daily hash and stores nothing on the device (applyConsent
  // below switches between the two)
  const isProduction = process.env.NODE_ENV === 'production'
  const consented = getConsent() === CONSENT_ACCEPTED
  const authId = localStorage.getItem('auth-id')
  posthog.init('phc_OuTrkPaB39hvpJosT8HgLakITPd5obj8QEtWvW2nA61', {
    api_host: 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: isProduction,
    capture_pageview: false, // we handle this manually via router
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    bootstrap:
      consented && authId
        ? { distinctID: authId.toString(), isIdentifiedID: true }
        : undefined
  })
  // super properties: attached to every event, incl. autocapture/$pageview.
  // The analytics ETL filters on platform and $user_id.
  posthog.register({
    environment: isProduction ? 'production' : 'staging',
    platform: 'web'
  })
  if (consented && authId) {
    posthog.register({ $user_id: authId.toString() })
  }
}

Vue.config.productionTip = false

Vue.component('datetime', Datetime)
Vue.use(VueMeta)
Vue.use(VModal, { dialog: true })
Vue.use(VueGoogleMaps, {
  load: {
    key: config.googleMapsApiKey
    // libraries: 'places'
  }
})

// gtag script is not loaded here (bootstrap: false); consent.js bootstraps it
// once the user has accepted the cookie dialog
Vue.use(
  VueGtag,
  {
    config: {
      id: 'G-YY7DFNMKHW'
    },
    bootstrap: false,
    enabled: process.env.NODE_ENV === 'production'
  },
  router
)

// re-apply the stored cookie choice from a previous visit. Must run AFTER
// Vue.use(VueGtag) above: applyConsent may bootstrap gtag, which crashes when
// the plugin options are not yet installed (broke the whole app for visitors
// with stored consent).
if (process.env.NODE_ENV !== 'development') {
  applyConsent(getConsent())
}

// track pageviews in posthog: identified with consent, otherwise PostHog's
// cookieless mode counts them anonymously
if (process.env.NODE_ENV !== 'development') {
  router.afterEach((to) => {
    if (hasConsent()) {
      // re-identify on each navigation in case session was reset
      const currentAuthId = localStorage.getItem('auth-id')
      if (currentAuthId && posthog.get_distinct_id() !== currentAuthId) {
        analytics.identify(currentAuthId)
      }
    }
    posthog.capture('$pageview', {
      $current_url: window.location.origin + to.fullPath
    })
  })
}

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App)
}).$mount('#app')
